import React from "react";
import { Button, Box, useMediaQuery, useTheme } from "@mui/material";
import { styled } from "@mui/system";
import { Images } from "../../../utils/Images";

/*
  MOBILE RESPONSIVE BUTTONS
  
  All buttons automatically adapt to mobile screens (below 600px) with:
  - Reduced padding and dimensions for better mobile fit
  - Smaller font sizes for mobile readability (11px vs 14px)
  - Touch-friendly minimum heights (36px for mobile, 44px+ for desktop)
  - Reduced icon sizes and gaps
  - Compact mobile dimensions (120px width for donate button vs 160px)
  - Maintained desktop appearance on larger screens
  
  Two usage methods:
  1. Manual: Pass isMobile prop to each button
  2. Hook-based: Use useResponsiveButtons() hook (recommended)
*/

// Custom Arrow Up Right Icon Component using the SVG file from assets
const ArrowUpRightIcon = ({ isMobile }) => (
  <img
    src={Images.arrowUpRight}
    alt="arrow up right"
    width={isMobile ? "14" : "20"}
    height={isMobile ? "14" : "20"}
    style={{ filter: "brightness(0) invert(1)" }} // This makes the icon white
  />
);

// Campaign Button Variant - Dark button with arrow icon
const CampaignButton = styled(Button)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : "10px",
  padding: isMobile ? "10px 16px" : "13px 24px",
  borderRadius: "10px",
  backgroundColor: "#1D1D1B",
  color: "#FFFFFF",
  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  fontFamily: "Red Hat Display, sans-serif",
  fontSize: isMobile ? "11px" : "14px",
  fontWeight: 600,
  textTransform: "none",
  transition: "all 0.3s ease",
  whiteSpace: "nowrap",
  minWidth: isMobile ? "120px" : "183px", // Compact width for mobile
  minHeight: isMobile ? "36px" : "auto", // Touch-friendly minimum height for mobile
  "&:hover": {
    backgroundColor: "#2a2a28",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
  },
  "&:active": {
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    transform: "translateY(1px)",
  },
  "&:focus": {
    outline: "2px solid #4a90e2",
    outlineOffset: "2px",
  },
}));


// CampaignButtonNoArrow Variant - Dark button without arrow icon

const CampaignButtonNoArrow = styled(Button)(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : "10px",
  padding: isMobile ? "10px 16px" : "18px 24px",
  borderRadius: "10px",
  backgroundColor: "#1D1D1B",
  color: "#FFFFFF",
  boxShadow: "0 4px 0 0 #B9B9B8",
  fontFamily: "Red Hat Display, sans-serif",
  fontSize: isMobile ? "11px" : "14px",
  fontWeight: 600,
  lineHeight: isMobile ? "11px" : "14px",
  textTransform: "none",
  transition: "all 0.3s ease",
  whiteSpace: "nowrap",
  minHeight: isMobile ? "36px" : "auto",
  "&:hover": {
    backgroundColor: "#2a2a28",
    boxShadow: "0 6px 12px rgba(0, 0, 0, 0.15)",
  },
  "&:active": {
    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
    transform: "translateY(1px)",
  },
  "&:focus": {
    outline: "2px solid #4a90e2",
    outlineOffset: "2px",
  },
}));
const DonateButton = styled(Button)(({ theme, isMobile, isChampion,fullwidth  }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",

  // ✅ Adjust padding with isMobile + isChampion
  padding: isMobile
    ? isChampion
      ? "6px 12px"
      : "8px 16px"
    : isChampion
    ? "12px 20px"
    : "15px 24px",

  // ✅ Adjust width only with isChampion
   width:isMobile?"115px": fullwidth ? "100%" : isChampion ? "120px" : "160px",
  height: isMobile ? "36px" : "44px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 600,
  fontSize: isMobile ? "11px" : "14px",
  lineHeight: isMobile ? "11px" : "14px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "4px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",

  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
}));

// Donate Button with Arrow - Red button with shadow effect and 214px width
const DonateButtonWithArrow = styled(Button)(({ theme, isMobile }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  padding: isMobile ? "8px 16px" : "15px 24px",
  width: isMobile ? "182px" : "214px",
  height: isMobile ? "36px" : "48px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 700,
  fontSize: isMobile ? "12px" : "14px",
  lineHeight: isMobile ? "11px" : "14px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "4px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
}));
// Donate Button with Arrow - Red button with shadow effect and 214px width
const DonateButtonWithArrowTwo = styled(Button)(({ theme, isMobile }) => ({
  backgroundColor: "#F04951",
  color: "#FFFFFF",
  borderRadius: "10px",
  boxShadow: "0px 4px 0px #141414",
  padding: isMobile ? "8px 16px" : "15px 24px",
  width: isMobile ? "136px" : "183px",
  height: isMobile ? "36px" : "48px",
  fontFamily: "Red Hat Display, sans-serif",
  fontWeight: 700,
  fontSize: isMobile ? "12px" : "18px",
  lineHeight: isMobile ? "11px" : "14px",
  textAlign: "center",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "4px" : "8px",
  textTransform: "none",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: "#d63f46",
    boxShadow: "0px 4px 0px #141414",
  },
  "&:active": {
    boxShadow: "0px 2px 0px #141414",
    transform: "translateY(2px)",
  },
}));


// Custom Icon Button Variant - You can pass any icon
const CustomIconButton = styled(Button)(({ theme, variant, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: isMobile ? "6px" : "10px",
  padding: isMobile ? "8px 16px" : "14px 24px",
  backgroundColor: variant === "wishlist" ? "#FF4081" : "#1D1D1B",
  borderRadius: "8px",
  maxWidth: isMobile ? "160px" : "200px",
  minHeight: isMobile ? "36px" : "48px", // Touch-friendly minimum height for mobile
  fontFamily: "Red Hat Display, sans-serif",
  fontSize: isMobile ? "11px" : "14px",
  fontWeight: 600,
  lineHeight: isMobile ? "11px" : "14px",
  textTransform: "capitalize",
  color: "#FFFFFF",
  transition: "all 0.3s ease",
  "&:hover": {
    backgroundColor: variant === "wishlist" ? "#F50057" : "#2C2C2A",
    transform: "translateY(-2px)",
  },
  "&:active": {
    transform: "translateY(1px)",
  },
  "&:focus": {
    outline: "2px solid #4CAF50",
    outlineOffset: "2px",
  },
  "& svg": {
    width: isMobile ? "14px" : "18px",
    height: isMobile ? "14px" : "18px",
    color: "#FFFFFF",
  },
}));

// Custom Icon Button Component
const CustomIconButtonVariant = ({
  text,
  onClick,
  icon: CustomIcon,
  isMobile,
}) => {
  return (
    <CustomIconButton onClick={onClick} isMobile={isMobile}>
      {text}
      {CustomIcon && <CustomIcon size={isMobile ? 16 : 20} />}
    </CustomIconButton>
  );
};

// Campaign Button Component
const CampaignButtonVariant = ({ text, onClick, isMobile }) => {
  return (
    <CampaignButton onClick={onClick} isMobile={isMobile}>
      {text}
      <ArrowUpRightIcon isMobile={isMobile} />
    </CampaignButton>
  );
};
// Campaign Button Component without Arrow
const CampaignButtonNoArrowVariant = ({ text, onClick, isMobile }) => {
  return (
    <CampaignButtonNoArrow onClick={onClick} isMobile={isMobile}>
      {text}
    </CampaignButtonNoArrow>
  );
};

// Donate Button Component
const DonateButtonVariant = ({ text, onClick, isMobile, isChampion }) => {
  return (
    <DonateButton onClick={onClick} isMobile={isMobile} isChampion={isChampion}>
      {text}
    </DonateButton>
  );
};

// Donate Button with Arrow Icon Component
const DonateButtonWithArrowVariant = ({ text, onClick, isMobile }) => {
  return (
    <DonateButtonWithArrow onClick={onClick} isMobile={isMobile}>
      {text}
      <ArrowUpRightIcon isMobile={isMobile} />
    </DonateButtonWithArrow>
  );
};
// Donate Button with Arrow Icon Component
const DonateButtonWithArrowVariantTwo = ({ text, onClick, isMobile }) => {
  return (
    <DonateButtonWithArrowTwo onClick={onClick} isMobile={isMobile}>
      {text}
      <ArrowUpRightIcon isMobile={isMobile} />
    </DonateButtonWithArrowTwo>
  );
};

// Hook-based responsive button components
const useResponsiveButtons = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const ResponsiveCampaignButton = ({ text, onClick }) => (
    <CampaignButtonVariant text={text} onClick={onClick} isMobile={isMobile} />
  );

  // 🔥 Now supports champion variant
  const ResponsiveDonateButton = ({ text, onClick, isChampion = false }) => (
    <DonateButtonVariant
      text={text}
      onClick={onClick}
      isMobile={isMobile}
      isChampion={isChampion}
    />
  );

  const ResponsiveDonateButtonWithArrow = ({ text, onClick }) => (
    <DonateButtonWithArrowVariant
      text={text}
      onClick={onClick}
      isMobile={isMobile}
    />
  );

  const ResponsiveCustomIconButton = ({ text, onClick, icon }) => (
    <CustomIconButtonVariant
      text={text}
      onClick={onClick}
      icon={icon}
      isMobile={isMobile}
    />
  );

  return {
    ResponsiveCampaignButton,
    ResponsiveCampaignButtonNoArrow,
    ResponsiveDonateButton,
    ResponsiveDonateButtonWithArrow,
    ResponsiveCustomIconButton,
    isMobile,
  };
};

/* 
  HOW TO ADD ANOTHER CUSTOM BUTTON VARIANT:
  
  Step 1: Create a styled component
  const YourButtonName = styled(Button)(({ theme }) => ({
    // Add your custom styles here
    backgroundColor: "#your-color",
    color: "#text-color",
    padding: "your-padding",
    borderRadius: "your-radius",
    // ... other styles
    "&:hover": {
      backgroundColor: "#hover-color",
    },
    "&:active": {
      // active state styles
    },
  }));

  Step 2: Create the button component
  const YourButtonVariant = ({ text, onClick, icon: CustomIcon }) => {
    return (
      <YourButtonName onClick={onClick}>
        {CustomIcon && <CustomIcon size={20} />}
        {text}
      </YourButtonName>
    );
  };

  Step 3: Export it (add to exports below)
  
  Example Usage:
  <YourButtonVariant 
    text="Your Button Text" 
    onClick={yourClickHandler} 
    icon={YourIcon} // optional if you want custom icon
  />
*/

export {
  CampaignButtonVariant,
  CampaignButtonNoArrowVariant, 
  DonateButtonVariant,
  DonateButtonWithArrowVariant,
  DonateButtonWithArrowVariantTwo,
  CustomIconButtonVariant,
  useResponsiveButtons,
};

/*
  HOW TO USE THESE BUTTONS IN ANY PAGE:

  METHOD 1: Manual Responsive (Pass isMobile prop)
  Step 1: Import the buttons you need
  import { CampaignButtonVariant, DonateButtonVariant, CustomIconButtonVariant } from './Button.jsx';
  import { BsHeart, BsDownload } from "react-icons/bs"; // for custom icons
  import { useMediaQuery, useTheme } from '@mui/material';

  Step 2: Use in your component
  const MyPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleCampaignClick = () => {
      console.log('Campaign clicked');
    };

    const handleDonateClick = () => {
      console.log('Donate clicked');
    };

    const handleCustomClick = () => {
      console.log('Custom button clicked');
    };

    return (
      <div>
        <CampaignButtonVariant 
          text="View Campaigns" 
          onClick={handleCampaignClick}
          isMobile={isMobile}
        />
        
        <DonateButtonVariant 
          text="Donate Now" 
          onClick={handleDonateClick}
          isMobile={isMobile}
        />
        
        <CustomIconButtonVariant 
          text="Like This" 
          onClick={handleCustomClick}
          icon={BsHeart}
          isMobile={isMobile}
        />
        
        <CustomIconButtonVariant 
          text="Download" 
          onClick={handleCustomClick}
          icon={BsDownload}
          isMobile={isMobile}
        />
      </div>
    );
  };

  METHOD 2: Hook-based Responsive (Recommended)
  Step 1: Import the hook
  import { useResponsiveButtons } from './Button.jsx';
  import { BsHeart, BsDownload } from "react-icons/bs";

  Step 2: Use in your component
  const MyPage = () => {
    const { ResponsiveCampaignButton, ResponsiveDonateButton, ResponsiveCustomIconButton } = useResponsiveButtons();

    const handleCampaignClick = () => {
      console.log('Campaign clicked');
    };

    const handleDonateClick = () => {
      console.log('Donate clicked');
    };

    const handleCustomClick = () => {
      console.log('Custom button clicked');
    };

    return (
      <div>
        <ResponsiveCampaignButton 
          text="View Campaigns" 
          onClick={handleCampaignClick} 
        />
        
        <ResponsiveDonateButton 
          text="Donate Now" 
          onClick={handleDonateClick} 
        />
        
        <ResponsiveCustomIconButton 
          text="Like This" 
          onClick={handleCustomClick}
          icon={BsHeart}
        />
        
        <ResponsiveCustomIconButton 
          text="Download" 
          onClick={handleCustomClick}
          icon={BsDownload}
        />
      </div>
    );
  };
*/
